<script setup lang="ts">
import { computed, nextTick, shallowRef, useId } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used by the Vue template
import CustomerSessionPass from '@/features/session-pass/CustomerSessionPass.vue';
import { resolveSessionPassTheme } from '@/features/session-pass/style-profile';
import type { CustomerSessionPassData } from './useCustomerDownloadCodePage';

const props = defineProps<{
  sessionPass: CustomerSessionPassData | null;
  hasPortraits: boolean;
  hasExtras?: boolean;
}>();

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const id = useId();
type Tab = 'photos' | 'extras' | 'pass';
const showsExtras = computed(() =>
  Boolean(props.sessionPass && props.hasExtras)
);
const tabs = computed<Tab[]>(() => [
  ...(props.hasPortraits ? ['photos' as const] : []),
  ...(showsExtras.value ? ['extras' as const] : []),
  ...(props.sessionPass ? ['pass' as const] : []),
]);
const activeTab = shallowRef<Tab>('photos');
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const selectedTab = computed(() => {
  if (tabs.value.includes(activeTab.value)) {
    return activeTab.value;
  }
  return props.sessionPass ? 'pass' : 'photos';
});
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const showsTabs = computed(() =>
  Boolean(props.sessionPass && (props.hasPortraits || showsExtras.value))
);
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const theme = computed(() =>
  resolveSessionPassTheme(
    props.sessionPass?.styleProfile,
    props.sessionPass?.baseTheme
  )
);

async function selectTab(tab: Tab, event: Event): Promise<void> {
  const container = (event.currentTarget as HTMLElement).closest<HTMLElement>(
    '.CustomerDownloadTabs'
  );
  const shouldScroll = container && container.getBoundingClientRect().top < 0;
  activeTab.value = tab;
  if (shouldScroll) {
    await nextTick();
    container.scrollIntoView({ block: 'start', behavior: 'instant' });
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
function handleTabKey(event: KeyboardEvent, tab: Tab): void {
  let index = tabs.value.indexOf(tab);
  switch (event.key) {
    case 'ArrowLeft':
      index = (index - 1 + tabs.value.length) % tabs.value.length;
      break;
    case 'ArrowRight':
      index = (index + 1) % tabs.value.length;
      break;
    case 'Home':
      index = 0;
      break;
    case 'End':
      index = tabs.value.length - 1;
      break;
    default:
      return;
  }
  const next = tabs.value[index];
  if (!next) {
    return;
  }
  event.preventDefault();
  void selectTab(next, event);
  const button = event.currentTarget as HTMLButtonElement;
  button.parentElement
    ?.querySelector<HTMLButtonElement>(`[data-tab="${next}"]`)
    ?.focus({ preventScroll: true });
}
</script>

<template>
  <div class="CustomerDownloadTabs" :class="{ 'CustomerDownloadTabs--themed': sessionPass }">
    <div
      v-if="showsTabs"
      class="CustomerDownloadTabs__tabs"
      role="tablist"
      :aria-label="showsExtras ? $t('CustomerDigitalExtras.navigation') : undefined"
      :style="{
        '--DownloadTabs-accent': theme.accent,
        '--DownloadTabs-surface': theme.surface,
        '--DownloadTabs-text': theme.text,
        '--DownloadTabs-font': theme.bodyFont,
      }"
    >
      <button
        v-for="tab in tabs"
        :id="`${id}-${tab}-tab`"
        :key="tab"
        type="button"
        role="tab"
        :data-tab="tab"
        :data-testid="`customer-download-${tab}-tab`"
        :aria-selected="selectedTab === tab"
        :aria-controls="`${id}-${tab}-panel`"
        :tabindex="selectedTab === tab ? 0 : -1"
        @click="selectTab(tab, $event)"
        @keydown="handleTabKey($event, tab)"
      >
        {{ $t(showsExtras ? `CustomerDigitalExtras.tabs.${tab}` : `CustomerSessionPass.tabs.${tab}`) }}
      </button>
    </div>

    <div
      v-if="sessionPass"
      v-show="selectedTab === 'pass'"
      :id="`${id}-pass-panel`"
      class="CustomerDownloadTabs__pass"
      :role="showsTabs ? 'tabpanel' : undefined"
      :aria-labelledby="showsTabs ? `${id}-pass-tab` : undefined"
      :tabindex="showsTabs ? 0 : undefined"
    >
      <slot v-if="showsExtras && selectedTab === 'pass'" name="order" />
      <CustomerSessionPass
        v-bind="sessionPass"
        :has-portraits="hasPortraits"
      />
    </div>

    <div
      v-if="hasPortraits"
      v-show="!showsTabs || selectedTab === 'photos'"
      :id="`${id}-photos-panel`"
      class="CustomerDownloadTabs__photos"
      :role="showsTabs ? 'tabpanel' : undefined"
      :aria-labelledby="showsTabs ? `${id}-photos-tab` : undefined"
      :tabindex="showsTabs ? 0 : undefined"
    >
      <slot />
    </div>

    <div
      v-if="showsExtras"
      v-show="selectedTab === 'extras'"
      :id="`${id}-extras-panel`"
      role="tabpanel"
      :aria-labelledby="`${id}-extras-tab`"
      tabindex="0"
    >
      <slot v-if="selectedTab === 'extras'" name="extras" />
    </div>
  </div>
</template>

<style scoped>
.CustomerDownloadTabs,
.CustomerDownloadTabs__photos,
.CustomerDownloadTabs__pass {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(16px, 2vw, 24px);
  min-width: 0;
  width: 100%;
}

.CustomerDownloadTabs__tabs {
  display: flex;
  width: min(34rem, 100%);
  margin-inline: auto;
  padding: 0.25em;
  gap: 0.25em;
  border: 1px solid color-mix(in srgb, var(--DownloadTabs-text) 18%, transparent);
  border-radius: 0.8em;
  background: var(--DownloadTabs-surface);
  font-family: var(--DownloadTabs-font);
  font-size: clamp(14px, 1rem, 18px);
  text-shadow: none;
}

.CustomerDownloadTabs__tabs button {
  flex: 1 1 0;
  min-width: 0;
  min-height: 44px;
  padding: 0.7em 1em;
  border: 0;
  border-radius: 0.55em;
  color: var(--DownloadTabs-text);
  background: transparent;
  font: inherit;
  font-weight: 600;
  line-height: 1.25;
  cursor: pointer;
}

.CustomerDownloadTabs__tabs button[aria-selected="true"] {
  color: var(--DownloadTabs-accent);
  background: color-mix(in srgb, var(--DownloadTabs-accent) 10%, var(--DownloadTabs-surface));
}

.CustomerDownloadTabs__tabs button:focus-visible {
  outline: 2px solid var(--DownloadTabs-accent);
  outline-offset: -2px;
}

.CustomerDownloadTabs--themed .CustomerDownloadTabs__tabs {
  position: sticky;
  top: max(8px, env(safe-area-inset-top));
  z-index: 5;
  width: 100%;
  gap: 4px;
  padding: 5px;
  border: 1px solid var(--Extras-line);
  border-radius: 16px;
  background: var(--Extras-surface);
  font: 600 13px / 1.3 var(--Download-body-font);
  box-shadow: 0 4px 16px color-mix(in srgb, var(--Extras-text) 8%, transparent);
}

.CustomerDownloadTabs.CustomerDownloadTabs--themed { gap: 18px; align-content: start; scroll-margin-top: max(8px, env(safe-area-inset-top)); }

.CustomerDownloadTabs--themed .CustomerDownloadTabs__tabs button {
  min-height: 48px;
  padding: 10px 8px;
  border-radius: 11px;
  color: var(--Extras-text);
}

.CustomerDownloadTabs--themed .CustomerDownloadTabs__tabs button[aria-selected='true'] {
  color: var(--Extras-on-accent);
  background: var(--Extras-accent);
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.CustomerDownloadTabs--themed .CustomerDownloadTabs__tabs button:focus-visible {
  outline: 3px solid var(--Extras-secondary);
  outline-offset: -3px;
}
</style>
